import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { API_URL, token } from "./config";
import Swal from "sweetalert2";
import { BarLoader, ClipLoader } from "react-spinners";

const Login = () => {
    const navigate = useNavigate(); 
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [otp, setOtp] = useState('');
    const [vOtp, setVOtp] = useState('');
    const [emailSent, setEmailSent] = useState(false);
    const [newPass, setNewPass] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [originalOtp, setOriginalOtp] = useState(''); // Store original OTP for verification
    const [formData2, setFormData2] = useState({
        user_email: '',
        otp: ''
    })

    // Simple encryption/decryption functions
    const encryptData = (data) => {
        try {
            const key = "BlueOceanElearning2026!@#";
            const jsonString = JSON.stringify(data);
            let encrypted = '';
            for (let i = 0; i < jsonString.length; i++) {
                const charCode = jsonString.charCodeAt(i) ^ key.charCodeAt(i % key.length);
                encrypted += String.fromCharCode(charCode);
            }
            return btoa(encrypted);
        } catch (error) {
            console.error('Encryption error:', error);
            return null;
        }
    };

    const decryptData = (encryptedData) => {
        try {
            if (!encryptedData) return null;
            const decoded = atob(encryptedData);
            const key = "BlueOceanElearning2026!@#";
            let decrypted = '';
            for (let i = 0; i < decoded.length; i++) {
                const charCode = decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length);
                decrypted += String.fromCharCode(charCode);
            }
            return JSON.parse(decrypted);
        } catch (error) {
            console.error('Decryption error:', error);
            return null;
        }
    };

    // Check if saved state is expired
    const isStateExpired = (savedState) => {
        if (!savedState || !savedState.timestamp) return true;
        const now = Date.now();
        const expiryTime = 30 * 60 * 1000;
        return (now - savedState.timestamp) > expiryTime;
    };

    // Save state to localStorage with encryption and timestamp
    const saveStateToLocalStorage = (state) => {
        const stateWithTimestamp = {
            ...state,
            timestamp: Date.now()
        };
        const encryptedState = encryptData(stateWithTimestamp);
        if (encryptedState) {
            localStorage.setItem('forgotPasswordState', encryptedState);
        }
    };

    // Load and decrypt state from localStorage
    const loadStateFromLocalStorage = () => {
        const encryptedState = localStorage.getItem('forgotPasswordState');
        if (!encryptedState) return null;
        
        const decryptedState = decryptData(encryptedState);
        if (!decryptedState) return null;
        
        if (isStateExpired(decryptedState)) {
            localStorage.removeItem('forgotPasswordState');
            return null;
        }
        
        return decryptedState;
    };

    // Load saved state from localStorage on component mount
    useEffect(() => {
        const savedState = loadStateFromLocalStorage();
        if (savedState) {
            setEmail(savedState.email || '');
            setOtp(savedState.otp || '');
            setOriginalOtp(savedState.otp || ''); // Store original OTP
            setEmailSent(savedState.emailSent || false);
            setNewPass(savedState.newPass || false);
            setShowAddModal(savedState.showAddModal || false);
            setFormData2({
                user_email: savedState.email || '',
                otp: savedState.otp || ''
            });
            
            const timeRemaining = (savedState.timestamp + (30 * 60 * 1000)) - Date.now();
            if (timeRemaining > 0 && timeRemaining < 5 * 60 * 1000) {
                setTimeout(() => {
                    Swal.fire({
                        text: "Your session will expire in 5 minutes. Please complete the process soon.",
                        icon: "warning",
                        timer: 5000,
                        showConfirmButton: true
                    });
                }, 1000);
            }
        }
    }, []);

    // Save state to localStorage whenever relevant state changes
    useEffect(() => {
        if (showAddModal) {
            const stateToSave = {
                email,
                otp: originalOtp || otp, // Save the original OTP
                emailSent,
                newPass,
                showAddModal
            };
            saveStateToLocalStorage(stateToSave);
        } else {
            localStorage.removeItem('forgotPasswordState');
        }
    }, [email, originalOtp, otp, emailSent, newPass, showAddModal]);

    const handleLogin = async (event) => {
        event.preventDefault();
        setIsLoading(true);
        const credObj = { email, password };

        try {
            const response = await fetch(`${API_URL}/onboarding/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(credObj)
            });

            const data = await response.json();
            sessionStorage.setItem('token', data.token)

            if (response.ok) {
                localStorage.setItem('user', JSON.stringify(data.user));
                const storedUser = JSON.stringify(data.user);
                const user = JSON.parse(storedUser);
                localStorage.setItem('userId', user.user_id)
                localStorage.setItem('userRole', user.role)
                checkAdmin(user.user_id);
            } else {
                Swal.fire({
                    text: "Incorrect Username or Password!",
                    icon: "error"
                });
                setIsLoading(false);
            }
        } catch (err) {
            Swal.fire({
                text: "Login failed, check your network connection!",
                icon: "error"
            });
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const handleLogout = () => {
            localStorage.clear();
            console.log("User logged out");
        };
        handleLogout();
    }, [])

    const checkAdmin = async (user_id) => {
        try {
            const response = await fetch(`${API_URL}/colleges/admin/${user_id}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token()}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            if (data.length >= 1) {
                localStorage.setItem('myChannel', data[0].name);
                localStorage.setItem('myChannelId', data[0].college_id);
                navigate('/mychannel');
            } else {
                navigate('/mycourses');
            }
        } catch (error) {
            console.error("Error fetching admin:", error);
        }
    };

    const generateOtp = () => {
        const min = 123456;
        const max = 987987;
        const random = min + (Math.floor(Math.random() * (max - min + 1)));
        const otpString = random.toString();
        console.log('Generated OTP for email: ', otpString);
        
        setOtp(otpString);
        setOriginalOtp(otpString); // Store the original OTP for verification
        setFormData2({ ...formData2, otp: random })
        
        // Save to localStorage
        if (showAddModal) {
            const savedState = loadStateFromLocalStorage();
            const stateToSave = {
                email,
                otp: otpString,
                emailSent,
                newPass,
                showAddModal
            };
            saveStateToLocalStorage(stateToSave);
        }
    }

    const validateOtp = (e) => {
        e.preventDefault();
        console.log('Entered OTP:', vOtp);
        console.log('Original OTP:', originalOtp);
        console.log('Current OTP state:', otp);
        
        // Use originalOtp for verification since it never changes after generation
        const isValid = originalOtp === vOtp;
        
        if (isValid) {
            console.log('OTP verified successfully');
            setEmailSent(false);
            setNewPass(true);
        } else {
            console.log('OTP mismatch');
            Swal.fire({
                text: "Incorrect OTP! Please check your email and try again.",
                icon: "error"
            });
        }
    }

    const forgotPassword = () => {
        setShowAddModal(true);
        setEmailSent(false);
        setNewPass(false);
        setConfirmPassword('');
        setPassword('');
        setEmail('');
        setVOtp('');
        setOtp('');
        setOriginalOtp('');
        
        // Generate OTP when opening modal
        setTimeout(() => {
            generateOtp();
        }, 100);
    }

    const resetPassword = async (e) => {
        e.preventDefault();
        
        if (password !== confirmPassword) {
            Swal.fire({
                text: "Passwords do not match!",
                icon: "error"
            });
            return;
        }
        
        const savedState = loadStateFromLocalStorage();
        if (!savedState) {
            Swal.fire({
                text: "Your session has expired. Please start the password reset process again.",
                icon: "warning"
            });
            setShowAddModal(false);
            localStorage.removeItem('forgotPasswordState');
            return;
        }
        
        setIsLoading(true);
        let formData = ({
            email: email,
            newPassword: password
        })
        try {
            const response = await fetch(`${API_URL}/onboarding/forgotpassword`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                Swal.fire({
                    text: "Password reset failed, check your network connection!",
                    icon: "error"
                });
                throw new Error('Failed to reset password');
            }
            Swal.fire({
                text: "Password reset successfully! You can proceed to login.",
                icon: "success"
            });
            setIsLoading(false);
            setShowAddModal(false);
            localStorage.removeItem('forgotPasswordState');

        } catch (err) {
            Swal.fire({
                text: "Password reset failed, check your network connection!",
                icon: "error"
            });
            setIsLoading(false);
        }
    }

    const sendOtp = async (e) => {
        e.preventDefault();
        
        if (!email) {
            Swal.fire({
                text: "Please enter your email address!",
                icon: "error"
            });
            return;
        }
        
        const savedState = loadStateFromLocalStorage();
        if (!savedState && showAddModal) {
            Swal.fire({
                text: "Your session has expired. Please start the password reset process again.",
                icon: "warning"
            });
            setShowAddModal(false);
            localStorage.removeItem('forgotPasswordState');
            return;
        }
        
        setIsLoading(true);
        
        // Use the original OTP for sending
        const otpToSend = originalOtp || otp;
        
        if (!otpToSend) {
            console.log('No OTP found, generating new one');
            generateOtp();
            // Wait a bit for the OTP to be generated
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        const finalOtp = originalOtp || otp;
        console.log('Sending OTP to email:', email);
        console.log('OTP being sent:', finalOtp);
        
        setFormData2({
            user_email: email,
            otp: parseInt(finalOtp)
        });

        try {
            const response = await fetch(`${API_URL}/mailer/forgotPassword`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_email: email, otp: finalOtp })
            });

            if (!response.ok) {
                Swal.fire({
                    text: "Email verification failed, check your network connection!",
                    icon: "error"
                });
                setIsLoading(false);
                throw new Error('Failed to send OTP');
            }

            setEmailSent(true);
            setIsLoading(false);
            
            Swal.fire({
                text: `OTP sent successfully to ${email}! Please check your email.`,
                icon: "success",
                timer: 3000,
                showConfirmButton: false
            });

        } catch (err) {
            Swal.fire({
                text: "Email verification failed, check your network connection!",
                icon: "error"
            });
            setIsLoading(false);
        }
    }

    useEffect(() => {
        setFormData2({
            user_email: email,
            otp: otp
        })
    }, [email, otp])

    // Add event listener to restore modal state on page visibility change
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden && showAddModal) {
                const savedState = loadStateFromLocalStorage();
                if (savedState) {
                    setEmail(savedState.email);
                    setOtp(savedState.otp);
                    setOriginalOtp(savedState.otp); // Restore original OTP
                    setEmailSent(savedState.emailSent);
                    setNewPass(savedState.newPass);
                    
                    const timeRemaining = (savedState.timestamp + (30 * 60 * 1000)) - Date.now();
                    if (timeRemaining > 0 && timeRemaining < 5 * 60 * 1000) {
                        Swal.fire({
                            text: "Your session will expire in 5 minutes. Please complete the process soon.",
                            icon: "warning",
                            timer: 5000,
                            showConfirmButton: true
                        });
                    }
                } else if (showAddModal) {
                    Swal.fire({
                        text: "Your session has expired. Please start the password reset process again.",
                        icon: "warning"
                    });
                    setShowAddModal(false);
                    localStorage.removeItem('forgotPasswordState');
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        let expiryInterval;
        if (showAddModal) {
            expiryInterval = setInterval(() => {
                const savedState = loadStateFromLocalStorage();
                if (!savedState && showAddModal) {
                    Swal.fire({
                        text: "Your session has expired. Please start the password reset process again.",
                        icon: "warning"
                    });
                    setShowAddModal(false);
                    localStorage.removeItem('forgotPasswordState');
                }
            }, 60000);
        }
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (expiryInterval) {
                clearInterval(expiryInterval);
            }
        };
    }, [showAddModal]);

    document.addEventListener(
        "contextmenu", function(e)
        {
            e.preventDefault();
        }, false
    )

    return (
        <html lang="en">
            <body>
                <div style={{ backgroundColor: 'rgb(246, 243, 237)', height: '100vh', margin: 0 }}>
                    <div className="container" style={{ height: '100%' }}>
                        <div className="row justify-content-center" style={{ height: '100%', alignItems: 'center' }}>
                            <div className="col-xl-10 col-lg-12 col-md-9">
                                <div className="card o-hidden border-0 shadow-lg my-5" >
                                    <div className="card-body p-0" style={{ height: '500px' }}>
                                        <div className="row">
                                            <div className="col-lg-6 d-none d-lg-block bg-login-image">
                                                <img src="../BlueOcean_logo.png" height="470px" width="520px" alt="Background" />
                                            </div>
                                            <div className="col-lg-6">
                                                <div className="p-5">
                                                    <div className="row text-center" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: 0 }}>
                                                        <div className="col-lg-8" >
                                                            <div>
                                                                <h1 className="h5 mb-1" style={{ margin: 0, color: 'blue' }}>E-LEARNING PORTAL</h1>
                                                            </div>
                                                            <h1 className="h5 text-gray-900 mb-4" style={{ margin: 0 }}>Sign In!</h1>
                                                        </div>
                                                    </div>

                                                    <form className="user" onSubmit={handleLogin}>
                                                        <div className="form-group">
                                                            <input
                                                                type="email"
                                                                className="form-control form-control-user custom-input"
                                                                id="exampleInputEmail"
                                                                aria-describedby="emailHelp"
                                                                placeholder="Enter Email Address..."
                                                                value={email}
                                                                onChange={(e) => setEmail(e.target.value)}
                                                                required
                                                                style={{ height: '50px' }}
                                                            />
                                                        </div>
                                                        <br />
                                                        <div className="form-group">
                                                            <input
                                                                type="password"
                                                                className="form-control form-control-user custom-input"
                                                                id="exampleInputPassword"
                                                                placeholder="Password"
                                                                value={password}
                                                                onChange={(e) => setPassword(e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <div className="custom-control custom-checkbox small custom-btn" style={{ float: 'left' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    className="custom-control-input"
                                                                    id="customCheck"
                                                                />
                                                                <label className="custom-control-label" htmlFor="customCheck" onClick={forgotPassword} style={{ cursor: 'pointer' }}>Forgot Password</label>
                                                            </div>
                                                        </div>
                                                        <br />
                                                        <button
                                                            className="btn btn-dark btn-user btn-block"
                                                            type="submit"
                                                            style={{ backgroundColor: 'rgba(32, 70, 161)', borderColor: 'rgba(32, 70, 161)', width: '100%' }} disabled={isLoading}
                                                        >
                                                            Login
                                                        </button>
                                                        <div className="text-center"><br></br>
                                                            <a className="small" style={{ color: 'black' }} href="/register">Create an Account!</a>
                                                        </div>
                                                    </form>
                                                    <hr />
                                                </div>
                                            </div>
                                            {isLoading && (
                                                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '5px' }}>
                                                    <BarLoader size={40} width={'100%'} color="blue" loading />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Forgot Password Modal */}
                {showAddModal && (
                    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => {
                        setShowAddModal(false);
                        localStorage.removeItem('forgotPasswordState');
                    }}>
                        <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-content" style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)', borderRadius: '8px' }}>
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        {!emailSent && !newPass && "Email Verification"}
                                        {emailSent && "Enter OTP"}
                                        {newPass && "Reset Password"}
                                    </h5>
                                    <button type="button" className="close" onClick={() => {
                                        setShowAddModal(false);
                                        localStorage.removeItem('forgotPasswordState');
                                    }}>&times;</button>
                                </div>
                                
                                {!emailSent && !newPass && (
                                    <form onSubmit={sendOtp}>
                                        <div className="modal-body">
                                            <div className="form-group">
                                                <label className="modal-label">Enter your email:</label>
                                                <input 
                                                    type="email" 
                                                    className="form-control" 
                                                    value={email} 
                                                    onChange={(e) => setEmail(e.target.value)} 
                                                    required 
                                                    autoFocus
                                                />
                                                <small className="form-text text-muted">
                                                    We'll send a 6-digit OTP to this email address (valid for 30 minutes)
                                                </small>
                                            </div>
                                        </div>
                                        <div className="modal-footer">
                                            <button type="button" className="btn btn-secondary" onClick={() => {
                                                setShowAddModal(false);
                                                localStorage.removeItem('forgotPasswordState');
                                            }}>Close</button>
                                            <button type="submit" className="btn btn-primary" disabled={isLoading}>
                                                {isLoading ? "Sending..." : "Send OTP"}
                                            </button>
                                        </div>
                                    </form>
                                )}

                                {emailSent && (
                                    <form onSubmit={validateOtp}>
                                        <div className="modal-body">
                                            <div className="form-group">
                                                <label className="modal-label">Enter OTP sent to ({email}):</label>
                                                <input 
                                                    type="text" 
                                                    className="form-control" 
                                                    value={vOtp} 
                                                    onChange={(e) => setVOtp(e.target.value)} 
                                                    required 
                                                    autoFocus
                                                    placeholder="Enter 6-digit OTP"
                                                    maxLength="6"
                                                />
                                                <small className="form-text text-muted">
                                                    Please enter the 6-digit code sent to your email
                                                </small>
                                            </div>
                                        </div>
                                        <div className="modal-footer">
                                            <button type="button" className="btn btn-secondary" onClick={() => {
                                                setShowAddModal(false);
                                                localStorage.removeItem('forgotPasswordState');
                                            }}>Close</button>
                                            <button type="submit" className="btn btn-primary" disabled={isLoading}>
                                                {isLoading ? "Verifying..." : "Verify OTP"}
                                            </button>
                                        </div>
                                    </form>
                                )}
                                
                                {newPass && (
                                    <form onSubmit={resetPassword}>
                                        <div className="modal-body">
                                            <div className="form-group">
                                                <label className="modal-label">Enter New Password:</label>
                                                <input 
                                                    type="password" 
                                                    className="form-control" 
                                                    value={password} 
                                                    onChange={(e) => setPassword(e.target.value)} 
                                                    required 
                                                    autoFocus
                                                    placeholder="New password"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label className="modal-label">Confirm Password:</label>
                                                <input 
                                                    type="password" 
                                                    className="form-control" 
                                                    value={confirmPassword} 
                                                    onChange={(e) => setConfirmPassword(e.target.value)} 
                                                    required 
                                                    placeholder="Confirm new password"
                                                />
                                            </div>
                                        </div>
                                        <div className="modal-footer">
                                            <button type="button" className="btn btn-secondary" onClick={() => {
                                                setShowAddModal(false);
                                                localStorage.removeItem('forgotPasswordState');
                                            }}>Close</button>
                                            <button type="submit" className="btn btn-primary" disabled={isLoading}>
                                                {isLoading ? "Resetting..." : "Reset Password"}
                                            </button>
                                        </div>
                                    </form>
                                )}
                                
                                {isLoading && (
                                    <div style={{ display: 'flex', justifyContent: 'center', margin: '10px' }}>
                                        <ClipLoader size={30} color="blue" loading />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </body>
        </html>
    );
};

export default Login;



// import React, { useEffect, useState } from "react";
// import { useNavigate } from 'react-router-dom';
// import { API_URL, token } from "./config";
// import Swal from "sweetalert2";
// import { BarLoader, ClipLoader } from "react-spinners";

// const Login = () => {
//     const navigate = useNavigate(); 
//     const [email, setEmail] = useState('');
//     const [password, setPassword] = useState('');
//     const [confirmPassword, setConfirmPassword] = useState('');
//     const [otp, setOtp] = useState('');
//     const [vOtp, setVOtp] = useState('');
//     const [emailSent, setEmailSent] = useState(false);
//     const [newPass, setNewPass] = useState(false);
//     const [showAddModal, setShowAddModal] = useState(false);
//     const [isLoading, setIsLoading] = useState(false);
//     const [formData2, setFormData2] = useState({
//         user_email: '',
//         otp: ''
//     })

//     const handleLogin = async (event) => {
//         event.preventDefault();
//         setIsLoading(true);
//         const credObj = { email, password };

//         try {
//             const response = await fetch(`${API_URL}/onboarding/login`, {
//                 method: "POST",
//                 headers: { "Content-Type": "application/json" },
//                 body: JSON.stringify(credObj)
//             });

//             const data = await response.json();
//             sessionStorage.setItem('token', data.token)

//             if (response.ok) {
//                 localStorage.setItem('user', JSON.stringify(data.user));

//                 const storedUser = JSON.stringify(data.user);
//                 const user = JSON.parse(storedUser);

//                 localStorage.setItem('userId', user.user_id)
//                 localStorage.setItem('userRole', user.role)
//                 checkAdmin(user.user_id);

//                 // if(user.role === "college"){
//                 //     navigate('/mychannel');
//                 // }else{
//                 //     navigate('/mycourses');
//                 // }
//             } else {
//                 Swal.fire({
//                     text: "Incorrect Username or Password!",
//                     icon: "error"
//                 });
//                 setIsLoading(false);
//             }
//         } catch (err) {
//             Swal.fire({
//                 text: "Login failed, check your network connection!",
//                 icon: "error"
//             });
//             setIsLoading(false);
//         }
//     };

//     useEffect(() => {
//         const handleLogout = () => {
//             localStorage.clear();
//             console.log("User logged out");
//         };

//         handleLogout();
//     }, [])

//     const checkAdmin = async (user_id) => {
//         try {
//             const response = await fetch(`${API_URL}/colleges/admin/${user_id}`, {
//                 method: 'GET',
//                 headers: {
//                     'Authorization': `Bearer ${token()}`,
//                     'Content-Type': 'application/json'
//                 }
//             });
//             if (!response.ok) {
//                 throw new Error('Network response was not ok');
//             }
//             const data = await response.json();
//             console.log(data)
//             console.log(data.length)
//             if (data.length >= 1) {
//                 localStorage.setItem('myChannel', data[0].name);
//                 localStorage.setItem('myChannelId', data[0].college_id);
//                 navigate('/mychannel');
//             } else {
//                 console.log("code executed")
//                 navigate('/mycourses');
//             }
//             console.log(data.length);
//         } catch (error) {
//             console.error("Error fetching admin:", error);
//         }
//     };

//     //NEW
//     const generateOtp = () => {

//         const min = 123456;
//         const max = 987987;
//         const random = min + (Math.floor(Math.random() * (max - min + 1)));
//         console.log('random number: ', random);
//         setOtp(random.toString());

//         setFormData2({ ...formData2, otp: random })
//     }

//     const validateOtp = (e) => {
//         e.preventDefault();
//         if (otp === vOtp) {
//             setEmailSent(false);
//             setNewPass(true);
//         } else {
//             console.log(otp)
//             console.log(vOtp)
//             Swal.fire({
//                 text: "Incorrect OTP",
//                 icon: "error"
//             });
//         }
//     }

//     const forgotPassword = () => {
//         setShowAddModal(true);
//         setEmailSent(false);
//         setNewPass(false);
//         setConfirmPassword('');
//         setPassword('');
//         setEmail('');
//         setVOtp('');
//     }

//     const resetPassword = async (e) => {
//         e.preventDefault();
//         setIsLoading(true);
//         let formData = ({
//             email: email,
//             newPassword: password
//         })
//         try {
//             const response = await fetch(`${API_URL}/onboarding/forgotpassword`, {
//                 method: "POST",
//                 headers: { "Content-Type": "application/json" },
//                 body: JSON.stringify(formData)
//             });

//             if (!response.ok) {
//                 Swal.fire({
//                     text: "Registration failed, check your network connection!",
//                     icon: "error"
//                 });
//                 throw new Error('Failed to register teacher');
//             }
//             Swal.fire({
//                 text: "Password reset successfully! You can proceed to login.",
//                 icon: "success"
//             });
//             setIsLoading(false);
//             setShowAddModal(false);

//             // setEmailSent(true);

//         } catch (err) {
//             Swal.fire({
//                 text: "Password reset failed, check your network connection!",
//                 icon: "error"
//             });
//             setIsLoading(false);
//         }
//     }

//     const sendOtp = async (e) => {
//         e.preventDefault();
//         setIsLoading(true);
//         // console.log(formData2);

//         try {
//             const response = await fetch(`${API_URL}/mailer/forgotPassword`, {
//                 method: "POST",
//                 headers: { "Content-Type": "application/json" },
//                 body: JSON.stringify(formData2)
//             });

//             if (!response.ok) {
//                 Swal.fire({
//                     text: "Email verification failed, check your network connection!",
//                     icon: "error"
//                 });
//                 setIsLoading(true);
//                 throw new Error('Failed to register teacher');
//             }

//             setEmailSent(true);
//             setIsLoading(false);

//         } catch (err) {
//             Swal.fire({
//                 text: "Email verification failed, check your network connection!",
//                 icon: "error"
//             });
//             setIsLoading(false);
//         }
//     }

//     useEffect(() => {
//         generateOtp();
//     }, [])

//     useEffect(() => {
//         setFormData2({
//             user_email: email,
//             otp: otp
//         })
//     }, [email])

//     document.addEventListener(
//         "contextmenu", function(e)
//         {
//             e.preventDefault();
//         }, false
//     )

//     return (
//         <html lang="en">
//             <body>

//                 <div style={{ backgroundColor: 'rgb(246, 243, 237)', height: '100vh', margin: 0 }}>
//                     <div className="container" style={{ height: '100%' }}>
//                         <div className="row justify-content-center" style={{ height: '100%', alignItems: 'center' }}>
//                             <div className="col-xl-10 col-lg-12 col-md-9">
//                                 <div className="card o-hidden border-0 shadow-lg my-5" >
//                                     <div className="card-body p-0" style={{ height: '500px' }}>
//                                         <div className="row">
//                                             <div className="col-lg-6 d-none d-lg-block bg-login-image">
//                                                 {/* <img src="../tantak_logo.png" height="480px" width="530px" alt="Background" /> */}<br></br>
//                                                 <img src="../BlueOcean_logo.png" height="470px" width="520px" alt="Background" />
//                                             </div>
//                                             <div className="col-lg-6">
//                                                 <div className="p-5">
//                                                     <div className="row text-center" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: 0 }}>
//                                                         <div className="col-lg-8" >
//                                                             <div>
//                                                                 {/* <img src="../assets/img/tantak-logo.png" height="50px" width="120px" alt="NLCC Logo" /> */}
//                                                                 <h1 className="h5 mb-1" style={{ margin: 0, color: 'blue' }}>E-LEARNING PORTAL</h1>
//                                                             </div>
//                                                             <h1 className="h5 text-gray-900 mb-4" style={{ margin: 0 }}>Sign In!</h1>
//                                                         </div>
//                                                     </div>

//                                                     <form className="user" onSubmit={handleLogin}>
//                                                         <div className="form-group">
//                                                             <input
//                                                                 type="email"
//                                                                 className="form-control form-control-user custom-input"
//                                                                 id="exampleInputEmail"
//                                                                 aria-describedby="emailHelp"
//                                                                 placeholder="Enter Email Address..."
//                                                                 value={email}
//                                                                 onChange={(e) => setEmail(e.target.value)}
//                                                                 required
//                                                                 style={{ height: '50px' }}
//                                                             />
//                                                         </div>
//                                                         <br />
//                                                         <div className="form-group">
//                                                             <input
//                                                                 type="password"
//                                                                 className="form-control form-control-user custom-input"
//                                                                 id="exampleInputPassword"
//                                                                 placeholder="Password"
//                                                                 value={password}
//                                                                 onChange={(e) => setPassword(e.target.value)}
//                                                                 required
//                                                             />
//                                                         </div>
//                                                         {/* <br /> */}
//                                                         <div className="form-group">
//                                                             <div className="custom-control custom-checkbox small custom-btn" style={{ float: 'left' }}>
//                                                                 <input
//                                                                     type="checkbox"
//                                                                     className="custom-control-input"
//                                                                     id="customCheck"
//                                                                 />
//                                                                 <label className="custom-control-label" htmlFor="customCheck" onClick={forgotPassword} style={{ cursor: 'pointer' }}>Forgot Password</label>
//                                                             </div>
//                                                         </div>
//                                                         <br />
//                                                         <button
//                                                             className="btn btn-dark btn-user btn-block"
//                                                             type="submit"
//                                                             // style={{ backgroundColor: 'rgba(17, 112, 63, 0.866)', borderColor: 'rgba(19, 171, 92, 0.866)', width: '100%' }}
//                                                             style={{ backgroundColor: 'rgba(32, 70, 161)', borderColor: 'rgba(32, 70, 161)', width: '100%' }} disabled={isLoading}
//                                                         >
//                                                             Login
//                                                         </button>
//                                                         <div className="text-center"><br></br>
//                                                             <a className="small" style={{ color: 'black' }} href="/register">Create an Account!</a>
//                                                         </div>
//                                                     </form>
//                                                     <hr />
//                                                 </div>
//                                                 {/* <br /><br /><br /><br /><br />   */}
//                                             </div>
//                                             {isLoading && (
//                                                 <div style={{ display: 'flex', justifyContent: 'center', marginTop: '5px' }}>
//                                                     <BarLoader size={40} width={'100%'} color="blue" loading />
//                                                 </div>
//                                             )}
//                                         </div>
//                                     </div>
//                                 </div>
//                             </div>
//                         </div>
//                     </div>
//                 </div>
//                 {/* Submit Assignment Modal */}
//                 {showAddModal && (
//                     <div className="modal fade show" style={{ display: 'block' }} onClick={() => setShowAddModal(false)}>
//                         <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
//                             <div className="modal-content" style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)', borderRadius: '8px' }}>
//                                 <div className="modal-header">
//                                     <h5 className="modal-title">Email verification</h5>
//                                     <button type="button" className="close" onClick={() => setShowAddModal(false)}>&times;</button>
//                                 </div>
//                                 {!emailSent && !newPass && (
//                                     <form onSubmit={sendOtp}>
//                                         <div className="modal-body">
//                                             <div className="form-group">
//                                                 <label className="modal-label">Enter your email:</label>
//                                                 <input type="email" className="form-control" value={email} onChange={(e) => setEmail(e.target.value)} required />
//                                             </div>
//                                         </div>
//                                         <div className="modal-footer">
//                                             <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Close</button>
//                                             {isLoading && (
//                                                 <div style={{ marginTop: '8px', textAlign: 'center' }}>
//                                                     <div className="btn btn-primary" style={{ width: '5rem' }}>
//                                                         <ClipLoader loading={isLoading} size={27} color="white" />
//                                                     </div>
//                                                 </div>
//                                             )}
//                                             {!isLoading && (
//                                                 <button type="submit" className="btn btn-primary">Submit</button>
//                                             )}
//                                         </div>
//                                         {isLoading && (
//                                             <div style={{ display: 'flex', justifyContent: 'center', marginTop: '5px' }}>
//                                                 <BarLoader size={40} width={'100%'} color="blue" loading />
//                                             </div>
//                                         )}
//                                     </form>
//                                 )}

//                                 {/* Enter OTP */}
//                                 {emailSent && (
//                                     <form onSubmit={validateOtp}>
//                                         <div className="modal-body">
//                                             <div className="form-group">
//                                                 <label className="modal-label">Enter OTP sent to ({email}):</label>
//                                                 <input type="number" className="form-control" value={vOtp} onChange={(e) => setVOtp(e.target.value)} required />
//                                             </div>
//                                         </div>
//                                         <div className="modal-footer">
//                                             <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Close</button>
//                                             {isLoading && (
//                                                 <div style={{ marginTop: '8px', textAlign: 'center' }}>
//                                                     <div className="btn btn-primary" style={{ width: '5rem' }}>
//                                                         <ClipLoader loading={isLoading} size={27} color="white" />
//                                                     </div>
//                                                 </div>
//                                             )}
//                                             {!isLoading && (
//                                                 <button type="submit" className="btn btn-primary">Submit</button>
//                                             )}
//                                         </div>
//                                         {isLoading && (
//                                             <div style={{ display: 'flex', justifyContent: 'center', marginTop: '5px' }}>
//                                                 <BarLoader size={40} width={'100%'} color="blue" loading />
//                                             </div>
//                                         )}
//                                     </form>
//                                 )}
//                                 {/* Enter OTP */}
//                                 {newPass && (
//                                     <form onSubmit={resetPassword}>
//                                         <div className="modal-body">
//                                             <div className="form-group">
//                                                 <label className="modal-label">Enter New Password:</label>
//                                                 <input type="password" className="form-control" value={password} onChange={(e) => setPassword(e.target.value)} required />
//                                             </div>
//                                         </div>
//                                         <div className="modal-body">
//                                             <div className="form-group">
//                                                 <label className="modal-label">Confirm Password</label>
//                                                 <input type="password" className="form-control" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
//                                             </div>
//                                         </div>
//                                         <div className="modal-footer">
//                                             <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Close</button>
//                                             {isLoading && (
//                                                 <div style={{ marginTop: '8px', textAlign: 'center' }}>
//                                                     <div className="btn btn-primary" style={{ width: '5rem' }}>
//                                                         <ClipLoader loading={isLoading} size={27} color="white" />
//                                                     </div>
//                                                 </div>
//                                             )}
//                                             {!isLoading && (
//                                                 <button type="submit" className="btn btn-primary">Submit</button>
//                                             )}
//                                         </div>
//                                         {isLoading && (
//                                             <div style={{ display: 'flex', justifyContent: 'center', marginTop: '5px' }}>
//                                                 <BarLoader size={40} width={'100%'} color="blue" loading />
//                                             </div>
//                                         )}
//                                     </form>
//                                 )}
//                             </div>
//                         </div>
//                     </div>
//                 )}
//             </body>
//         </html>
//     );
// };

// export default Login;
